import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserProfileChangeRequests,
  updateProfileChangeStatus,
} from "../../redux/Thunk/externalResourceThunk";
import { Box, Button, Card, CardHeader, CardContent, Typography, Chip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import SearchField from "../../Components/Ui_elements/SearchField";
import Papa from "papaparse";
import GenericTable from "../../Components/Ui_elements/GenericTable";
import PopupModal from "../../Components/Ui_elements/PopupModal";
import { Grid } from "@mui/material";
import Btn from "../../Components/Ui_elements/Btn";
import { formatDateTime } from "../../Components/formateDate";

const ProfileFieldUpdateRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileChangeRequestsList } = useSelector(state => state.root.ExternalResource);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState({ column: 1, dir: "asc" });
  const [openActionPopup, setOpenActionPopup] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const { user: loginUser } = useSelector(state => state.root.authUser);
  const isLinguist = loginUser?.iFkUserTypeId === 2; // linguist
  console.log("loginUser", loginUser);

  const handleSearch = value => {
    setSearchTerm(value);
  };

  const baseColumns = [
    {
      name: "rowNumber",
      label: "#",
      options: {
        sort: false,
        customBodyRender: (_, tableMeta) => page * pageSize + tableMeta.rowIndex + 1,
      },
    },
    {
      name: "fullName",
      label: "Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = profileChangeRequestsList?.data?.[tableMeta.rowIndex];

          return isLinguist ? (
            <Typography variant="body2" fontWeight={500}>
              {value}
            </Typography>
          ) : (
            <Link
              className="text-primary"
              style={{ textDecoration: "none", fontWeight: "500" }}
              to={`/viewExternal/${row?.user_id}`}
            >
              {value}
            </Link>
          );
        },
      },
    },
    { name: "field_name", label: "Field Request" },
    { name: "new_value", label: "Field Value" },
    {
      name: "reason",
      label: "Reason",
      options: {
        setCellProps: () => ({
          style: { width: "250px", maxWidth: "250px" },
        }),
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRender: value => {
          const statusMap = {
            pending: { label: "Pending", color: "warning" },
            approved: { label: "Approved", color: "success" },
            rejected: { label: "Rejected", color: "error" },
          };

          const status = statusMap[value] || {
            label: value,
            color: "default",
          };

          return <Chip size="small" label={status.label} color={status.color} variant="outlined" />;
        },
      },
    },
    {
      name: "created_at_formatted",
      label: "Created At",
      options: {
        customBodyRender: value => (
          <Typography variant="body2">{formatDateTime(value, true)}</Typography>
        ),
      },
    },
  ];

  const actionColumn = {
    name: "actions",
    label: "Actions",
    options: {
      sort: false,
      filter: false,
      customBodyRender: (_, tableMeta) => {
        const row = profileChangeRequestsList?.data?.[tableMeta.rowIndex];

        if (row?.status !== "pending") return "-";

        return (
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => {
                setSelectedRequest({ id: row.id, action: "approved" });
                setOpenActionPopup(true);
              }}
            >
              Approve
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => {
                setSelectedRequest({ id: row.id, action: "rejected" });
                setOpenActionPopup(true);
              }}
            >
              Reject
            </Button>
          </Box>
        );
      },
    },
  };

  const columns = isLinguist ? baseColumns : [...baseColumns, actionColumn];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    const request = {
      search: debouncedSearch,
      page: page + 1,
      perPage: pageSize,
      order: order.column ? [order] : [],
    };

    // 👇 if linguist → fetch ONLY their requests
    if (isLinguist) {
      request.loginUserId = loginUser?.iUserId;
    }

    dispatch(getUserProfileChangeRequests(request));
  }, [page, pageSize, order, debouncedSearch, isLinguist]);

  // Table change handler
  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        break;
      case "changeRowsPerPage":
        setPageSize(tableState.rowsPerPage);
        setPage(0);
        break;
      case "sort":
        setOrder({
          column: tableState.activeColumn,
          dir: tableState.sortOrder.direction,
        });
        break;
      default:
        break;
    }
  };

  return (
    <Card>
      <CardHeader
        title="Profile field update requests"
        action={
          <Box display="flex" alignItems="center" gap={2}>
            <SearchField
              onSearch={handleSearch}
              placeholder="Search..."
              size="small"
              initialValue={searchTerm}
            />
          </Box>
        }
      />
      <CardContent sx={{ padding: "0px!important" }}>
        <GenericTable
          columns={columns}
          data={profileChangeRequestsList?.data}
          showAction={false}
          options={{
            selectableRows: "none",
            serverSide: true,
            count: profileChangeRequestsList?.totalRecords || 0,
            page: page,
            rowsPerPage: pageSize,
            rowsPerPageOptions: [10, 25, 50, 100],
            search: false,
            download: false,
            print: false,
            filter: false,
            viewColumns: false,
            sortOrder: {
              name: columns[order?.column]?.name || "",
              direction: order.dir,
            },
            onTableChange: handleTableChange,
          }}
        />
      </CardContent>

      <PopupModal
        maxWidth={"sm"}
        size="sm"
        fullWidth
        open={openActionPopup}
        handleClose={() => {
          setOpenActionPopup(false);
          setSelectedRequest(null);
        }}
        title={
          <Typography variant="h6">
            {selectedRequest?.action === "approved" ? "Confirm Approval" : "Confirm Rejection"}
          </Typography>
        }
        actions={
          <Grid container columnGap={2} justifyContent="end">
            <Btn
              variant="contained"
              color={selectedRequest?.action === "approved" ? "success" : "error"}
              onClick={() => {
                dispatch(
                  updateProfileChangeStatus({
                    id: selectedRequest.id,
                    action: selectedRequest.action,
                  })
                ).then(() => {
                  setOpenActionPopup(false);
                  setSelectedRequest(null);

                  // refresh list
                  dispatch(
                    getUserProfileChangeRequests({
                      search: debouncedSearch,
                      page: page + 1,
                      perPage: pageSize,
                      order: order.column ? [order] : [],
                    })
                  );
                });
              }}
            >
              {selectedRequest?.action === "approved" ? "Confirm Approve" : "Confirm Reject"}
            </Btn>

            <Btn
              variant="outlined"
              onClick={() => {
                setOpenActionPopup(false);
                setSelectedRequest(null);
              }}
            >
              Cancel
            </Btn>
          </Grid>
        }
      >
        <Typography variant="body1">
          {selectedRequest?.action === "approved"
            ? "Are you sure you want to approve this profile change request?"
            : "Are you sure you want to reject this profile change request?"}
        </Typography>

        <Typography variant="body2" mt={1} color="text.secondary">
          This action cannot be undone.
        </Typography>
      </PopupModal>
    </Card>
  );
};

export default ProfileFieldUpdateRequests;
